import React from 'react'
import { Button } from '../ui/Button'
import { useAuth } from '../AuthProvider'
import { useI18n } from '../../i18n'
import { getActiveOrgId } from '../../utils/orgUtils'
import { captureSnapshot, fetchTrends, type TrendSeries } from '../../services/informationSystemPhase15Api'

const PERIODS = ['7d', '30d', '90d', '1y'] as const

interface Props {
  systemId: string
}

export default function OperationalTrendWidget({ systemId }: Props) {
  const { t } = useI18n()
  const { token } = useAuth()
  const orgId = getActiveOrgId()
  const [period, setPeriod] = React.useState<string>('30d')
  const [trends, setTrends] = React.useState<TrendSeries | null>(null)
  const [loading, setLoading] = React.useState(true)
  const [capturing, setCapturing] = React.useState(false)

  const load = React.useCallback(async () => {
    if (!token) return
    setLoading(true)
    try {
      const data = await fetchTrends(token, systemId, period, orgId)
      setTrends(data)
    } catch {
      setTrends(null)
    } finally {
      setLoading(false)
    }
  }, [token, systemId, period, orgId])

  React.useEffect(() => {
    load()
  }, [load])

  const vulnSeries = trends?.series?.vulnerabilities || []
  const ctrlSeries = trends?.series?.failedControls || []

  return (
    <div className="border rounded-lg p-4 bg-white space-y-3">
      <div className="flex flex-wrap justify-between gap-2 items-center">
        <h4 className="font-semibold text-gray-900">{t('assets.intelligence.trendsTitle', 'Operational trends')}</h4>
        <div className="flex gap-2 flex-wrap">
          {PERIODS.map((p) => (
            <button
              key={p}
              type="button"
              className={`text-xs px-2 py-1 rounded border ${period === p ? 'bg-blue-100 border-blue-300' : 'border-gray-200'}`}
              onClick={() => setPeriod(p)}
            >
              {t(`assets.intelligence.period.${p}`, p)}
            </button>
          ))}
          <Button
            type="button"
            size="small"
            color="outline"
            disabled={capturing}
            onClick={async () => {
              if (!token) return
              setCapturing(true)
              try {
                await captureSnapshot(token, systemId, orgId)
                await load()
              } finally {
                setCapturing(false)
              }
            }}
          >
            {t('assets.intelligence.captureSnapshot', 'Capture snapshot')}
          </Button>
        </div>
      </div>

      {loading ? (
        <p className="text-sm text-gray-500">{t('common.loading', 'Loading…')}</p>
      ) : trends?.note && !vulnSeries.length ? (
        <p className="text-sm text-gray-500">{trends.note}</p>
      ) : (
        <div className="grid gap-4 md:grid-cols-2 text-xs">
          <TrendMiniChart
            label={t('assets.intelligence.trendVulnerabilities', 'Critical/high vulnerabilities')}
            points={vulnSeries}
          />
          <TrendMiniChart
            label={t('assets.intelligence.trendFailedControls', 'Failed controls')}
            points={ctrlSeries}
          />
        </div>
      )}
    </div>
  )
}

function TrendMiniChart({ label, points }: { label: string; points: Array<{ date: string; value: number }> }) {
  const max = Math.max(1, ...points.map((p) => p.value))
  return (
    <div>
      <p className="font-medium text-gray-700 mb-2">{label}</p>
      {points.length === 0 ? (
        <p className="text-gray-400">—</p>
      ) : (
        <ul className="space-y-1">
          {points.slice(-8).map((p) => (
            <li key={p.date} className="flex items-center gap-2">
              <span className="w-20 text-gray-500 shrink-0">{new Date(p.date).toLocaleDateString()}</span>
              <span className="flex-1 h-2 bg-gray-100 rounded overflow-hidden">
                <span className="block h-full bg-red-500 rounded" style={{ width: `${(p.value / max) * 100}%` }} />
              </span>
              <span className="w-6 text-right">{p.value}</span>
            </li>
          ))}
        </ul>
      )}
    </div>
  )
}
